import React, { useState } from 'react';
import { Send, Trash2, MessageCircle, Mic } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../App';

const buildVoiceUrl = (noteId) => {
  const token = localStorage.getItem('qmg_token');
  return `/api/locker/coach-notes/${noteId}/voice?auth=${encodeURIComponent(token || '')}`;
};

const fmtDuration = (ms) => {
  if (!ms || ms <= 0) return null;
  const s = Math.round(ms / 1000);
  return `${s}s`;
};

const REACTION_OPTIONS = [
  { key: 'got_it', emoji: '🙏', label: 'Got it' },
  { key: 'on_it', emoji: '💪', label: 'On it' },
  { key: 'discuss', emoji: '❓', label: 'Discuss' },
];

/**
 * CoachNoteCard — renders a single coach note with reactions + replies thread.
 * Works for both player and coach views (the reply author is whoever's logged in).
 *
 * Props:
 *   - note: { id, coach_name, note, created_at, replies?, reactions? }
 *   - currentUserId: the logged-in user's id (used to highlight active reactions)
 *   - onChange: callback fired after a successful mutation; receives the updated note
 *   - compact: render in tighter mode (e.g. on LockerHome preview)
 *   - showReplyForm: default true; set false to hide reply box (read-only mode)
 *   - onDeleteNote: optional callback if the parent wants to expose delete
 */
const CoachNoteCard = ({
  note,
  currentUserId,
  onChange,
  compact = false,
  showReplyForm = true,
  onDeleteNote,
}) => {
  const [replyText, setReplyText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showReply, setShowReply] = useState(false);

  const reactions = note.reactions || {};
  const replies = note.replies || [];

  const handleReact = async (key) => {
    try {
      const { data } = await API.post(`/locker/coach-notes/${note.id}/react`, { key });
      onChange && onChange({ ...note, reactions: data.reactions });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to react.');
    }
  };

  const handleReply = async () => {
    const text = replyText.trim();
    if (!text) return;
    setSubmitting(true);
    try {
      const { data } = await API.post(`/locker/coach-notes/${note.id}/reply`, { text });
      setReplyText('');
      setShowReply(false);
      onChange && onChange(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to reply.');
    } finally {
      setSubmitting(false);
    }
  };

  const deleteReply = async (replyId) => {
    if (!window.confirm('Delete this reply?')) return;
    try {
      await API.delete(`/locker/coach-notes/${note.id}/replies/${replyId}`);
      onChange && onChange({ ...note, replies: replies.filter((r) => r.id !== replyId) });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete.');
    }
  };

  return (
    <div className={`border-l-2 border-[#BC562B] ${compact ? 'pl-3 py-1' : 'pl-4 py-2'}`} data-testid={`coach-note-${note.id}`}>
      {/* Header */}
      <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
        <p className="text-sm font-semibold text-[#1A1A1A]">{note.coach_name || 'Coach'}</p>
        <div className="flex items-center gap-2">
          <p className="text-xs text-[#737373]">{new Date(note.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</p>
          {onDeleteNote && (
            <button onClick={() => onDeleteNote(note.id)} className="text-[#BC562B] hover:text-[#9A4520]" title="Delete">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
      {/* Body */}
      {note.note && (
        <p className={`${compact ? 'text-xs' : 'text-sm'} text-[#57534E] whitespace-pre-wrap`}>{note.note}</p>
      )}

      {/* Voice memo */}
      {note.voice_path && (
        <div className="mt-2 flex items-center gap-2 bg-[#FFF9F5] border-l-2 border-[#BC562B] pl-2 py-1.5 pr-2" data-testid={`voice-note-${note.id}`}>
          <Mic className="w-3.5 h-3.5 text-[#BC562B] flex-shrink-0" />
          <audio
            controls
            preload="none"
            src={buildVoiceUrl(note.id)}
            className="h-8 flex-1 min-w-0"
            data-testid={`voice-audio-${note.id}`}
          />
          {fmtDuration(note.voice_duration_ms) && (
            <span className="text-[10px] text-[#BC562B] font-semibold tabular-nums flex-shrink-0">{fmtDuration(note.voice_duration_ms)}</span>
          )}
        </div>
      )}

      {/* Reactions */}
      <div className="mt-2 flex items-center gap-1.5 flex-wrap" data-testid={`reactions-${note.id}`}>
        {REACTION_OPTIONS.map((r) => {
          const list = reactions[r.key] || [];
          const isMine = list.includes(currentUserId);
          return (
            <button
              key={r.key}
              type="button"
              onClick={() => handleReact(r.key)}
              className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] border transition-colors ${
                isMine
                  ? 'bg-[#1A2E1D] text-white border-[#1A2E1D]'
                  : 'bg-white text-[#57534E] border-[#E7E5E4] hover:border-[#1A2E1D]/40'
              }`}
              data-testid={`react-${note.id}-${r.key}`}
              title={r.label}
            >
              <span>{r.emoji}</span>
              <span>{r.label}</span>
              {list.length > 0 && <span className="ml-1 font-semibold tabular-nums">{list.length}</span>}
            </button>
          );
        })}
        {showReplyForm && (
          <button
            type="button"
            onClick={() => setShowReply(!showReply)}
            className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] border bg-white text-[#57534E] border-[#E7E5E4] hover:border-[#1A2E1D]/40"
            data-testid={`reply-toggle-${note.id}`}
          >
            <MessageCircle className="w-3 h-3" />
            <span>Reply{replies.length > 0 ? ` · ${replies.length}` : ''}</span>
          </button>
        )}
      </div>

      {/* Reply thread */}
      {replies.length > 0 && (
        <ul className="mt-3 space-y-2 pl-3 border-l border-[#F2F0E9]" data-testid={`replies-${note.id}`}>
          {replies.map((r) => (
            <li key={r.id} className="text-xs" data-testid={`reply-${r.id}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold text-[#1A1A1A]">{r.author_name || 'You'}</span>
                <div className="flex items-center gap-2">
                  <span className="text-[#A8A29E]">{new Date(r.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</span>
                  {r.author_id === currentUserId && (
                    <button onClick={() => deleteReply(r.id)} className="text-[#BC562B] hover:text-[#9A4520]" title="Delete reply" data-testid={`delete-reply-${r.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
              <p className="text-[#57534E] whitespace-pre-wrap">{r.text}</p>
            </li>
          ))}
        </ul>
      )}

      {/* Reply form */}
      {showReplyForm && showReply && (
        <div className="mt-3 bg-[#FAFAF9] border border-[#E7E5E4] p-2" data-testid={`reply-form-${note.id}`}>
          <textarea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Write a reply…"
            rows={2}
            maxLength={2000}
            className="w-full text-xs bg-white border border-[#E7E5E4] p-2 focus:outline-none focus:border-[#1A2E1D]"
            data-testid={`reply-input-${note.id}`}
          />
          <div className="flex justify-end mt-1.5">
            <button
              onClick={handleReply}
              disabled={submitting || !replyText.trim()}
              className="inline-flex items-center gap-1 bg-[#1A2E1D] text-white text-[11px] font-semibold px-3 py-1.5 hover:bg-[#0F1F12] disabled:opacity-50"
              data-testid={`reply-send-${note.id}`}
            >
              <Send className="w-3 h-3" /> {submitting ? 'Sending…' : 'Send'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CoachNoteCard;
